import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../store/rootReducer';
import { useNavigate, useLocation } from 'react-router-dom';
import { InternalRoutes } from '../../../enum/InternalRoutes';
import { useTranslation } from 'react-i18next';
import { UserRoles } from '../../../enum/UserRoles';
import { SupportTutorialIcon } from '../../components/SupportTutorialIcon';
import SupportModal from '../../components/modal/SupportModal';
import { SupportWidget } from '../../SupportWidget';
import { BootstrapBreakpoints } from '../../../enum/BootstrapBreakpoints';
import { MobileMenu } from './MobileMenu';
import { agencyLinks, influencerLinks, brandLinks, globalBrandLinks } from '../../utils/MenuLinks';
import { Languages } from '../../utils/Languages';
import { UserService } from '../../../app/shared/user/service';
import { actions } from '../../../store/authentication/authRedux';
import { ErrorToast } from '../../../utils/toasters';
import CampaignLink from './components/CampaignLink';
import MenuLink from './components/MenuLink';

const LeftSideMenu = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const favoriteCampaigns = useSelector((state: IAllStates) => state.auth.user.favoriteCampaigns);
    const userType = useSelector((state: IAllStates) => state.auth.user.userType);
    const locale = useSelector((state: IAllStates) => state.auth.user.locale);

    const { AGENCY, AGENCY_MASTER, INFLUENCER, BRAND, BRAND_MASTER } = UserRoles;
    const isMobileScreen = window.innerWidth <= BootstrapBreakpoints.LG;
    const isGlobalBrandVisible = process.env.VITE_ENABLE_GLOBAL === 'true';
    const isGlobalBrandActive = useSelector((states: IAllStates) => states?.auth?.isGlobalBrand);

    const [isSupportOpened, setIsSupportOpened] = useState(false);
    const [isMenuOpen] = useState(true);
    const tabs = useMemo(() => {
        if (isGlobalBrandActive && userType === BRAND_MASTER) return globalBrandLinks.desktopLinks;
        if (userType === BRAND_MASTER) return brandLinks.desktopLinks;
        if (userType === INFLUENCER) return isMobileScreen ? influencerLinks.mobileLinks : influencerLinks.desktopLinks;
        if ([AGENCY, AGENCY_MASTER].includes(userType as UserRoles)) {
            return isMobileScreen ? agencyLinks.mobileLinks : agencyLinks.desktopLinks;
        }
        return [];
    }, [userType, isGlobalBrandActive]);

    const initialTabKey = tabs.find(tab => pathname.includes(tab.tabKey))?.tabKey ?? 'dashboard';
    const [activeTabKey, setActiveTabKey] = useState(initialTabKey);

    const isInfluencerMobileScreen = userType === INFLUENCER && isMobileScreen;
    const isAgencyMobileScreen = [AGENCY, AGENCY_MASTER].includes(userType as UserRoles) && isMobileScreen;
    const isMobileMenuVisible =
        userType === INFLUENCER || userType === AGENCY || userType === AGENCY_MASTER || isGlobalBrandActive;

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setActiveTabKey(newValue);
        if (userType === BRAND_MASTER) {
            isGlobalBrandActive
                ? navigate(`${InternalRoutes.BRAND_GLOBAL}/${newValue}`)
                : navigate(`${InternalRoutes.BRAND_CAMPAIGN}`);
        } else if (userType === INFLUENCER) {
            navigate(`${InternalRoutes.INFLUENCER_ROUTES}/${newValue}`);
        } else if ([AGENCY, AGENCY_MASTER].includes(userType as UserRoles)) {
            navigate(`${InternalRoutes.AGENCY_ROUTES}/${newValue}`);
        }
    };

    // const openLeftMenu = () => {
    //     setMenuOpen(!isMenuOpen);
    // };

    // useEffect(() => {
    //     if (userType === BRAND_MASTER) {
    //         setTabs(brandLinks.desktopLinks);
    //     } else if (userType === INFLUENCER) {
    //         setTabs(isMobileScreen ? influencerLinks.mobileLinks : influencerLinks.desktopLinks);
    //     } else if ([AGENCY, AGENCY_MASTER].includes(userType as UserRoles)) {
    //         setTabs(isMobileScreen ? agencyLinks.mobileLinks : agencyLinks.desktopLinks);
    //     }
    // }, [userType]);

    useEffect(() => {
        const currKey = String(pathname)?.split('/').at(-1) ?? 'dashboard';
        setActiveTabKey(currKey);
    }, [pathname]);

    const updateLanguage = (lang: string) => {
        UserService.updateGlobalPreference({ locale: lang })
            .then(data => {
                dispatch(actions.updateLanguage(data.locale));
            })
            .catch(error => ErrorToast(error));
    };

    return (
        <>
            <SupportModal show={isSupportOpened} closeModal={() => setIsSupportOpened(prev => !prev)} />
            <div className="position-relative z-index-2">
                <div
                    className={`left-side-bar ${
                        isInfluencerMobileScreen || isAgencyMobileScreen ? 'influencer-mobile' : ''
                    } flex-column align-items-center py-4  ${isMenuOpen ? 'opened' : 'closed'} height-95vh`}
                >
                    {/*<Button onClick={openLeftMenu} variant="outline" className="switch-btn">*/}
                    {/*  {isMenuOpen ? <VscEyeClosed size={15}/> : <VscEye size={15}/>}*/}
                    {/*</Button>*/}
                    <div className="option-bar h-100 d-flex flex-column align-items-center justify-content-between">
                        <div>
                            {(userType === BRAND_MASTER || userType === BRAND) && !isGlobalBrandActive
                                ? favoriteCampaigns?.map(campaign => (
                                      <CampaignLink key={campaign?.id} campaign={campaign} />
                                  ))
                                : tabs.map((tab, index) => (
                                      <MenuLink
                                          key={index}
                                          tab={tab}
                                          handleChange={handleChange}
                                          activeTabKey={activeTabKey}
                                      />
                                  ))}
                        </div>
                    </div>
                    {/* <div className="wrapper" style={{bottom: 180}}>
                        <ButtonTheme />
                    </div> */}
                    <Dropdown
                        drop="end"
                        className={`wrapper ${
                            ![AGENCY, AGENCY_MASTER].includes(userType as UserRoles) ? 'language' : 'tutorial'
                        }`}
                    >
                        <Dropdown.Toggle variant="white" id="dropdown-basic">
                            <h6 className="mb-0 text-uppercase text-primary" style={{ lineHeight: 'normale' }}>
                                {locale ?? 'en'}
                            </h6>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="min-width-min-content">
                            {Languages.map(lang => {
                                return (
                                    <Dropdown.Item onClick={() => updateLanguage(lang.value)} key={lang.value}>
                                        <div>{t(lang.label)}</div>
                                    </Dropdown.Item>
                                );
                            })}
                        </Dropdown.Menu>
                    </Dropdown>
                    <div className="wrapper">
                        <SupportWidget />
                        <div className="slider ">
                            <p>{t('general.button.contact/support')}</p>
                        </div>
                    </div>
                    {![AGENCY, AGENCY_MASTER].includes(userType as UserRoles) && (
                        <div className="wrapper tutorial">
                            <SupportTutorialIcon action={() => setIsSupportOpened(true)} />
                            <div className="slider ">
                                <p>{t('general.button.help/tutorials')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {isMobileMenuVisible && (
                <MobileMenu
                    tabs={tabs}
                    classes="d-lg-none shadow bg-white"
                    handleChange={handleChange}
                    activeTabKey={activeTabKey}
                />
            )}
        </>
    );
};

export default LeftSideMenu;
